/**
 * User-local calendar (same zone as Friday 15:00 check-in).
 * Callers declare field kind — formatters do not infer from value shape.
 */
export const LOCAL_TZ = 'America/Los_Angeles'

const WEEKDAY: Record<string, number> = {
  Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6,
}

export function localParts(now: Date | number | string = Date.now(), timeZone = LOCAL_TZ) {
  const d = instantDate(now)
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    weekday: 'short',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(d)
  const get = (type: string) => parts.find((p) => p.type === type)?.value
  let hour = Number(get('hour'))
  if (hour === 24) hour = 0
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    hour,
    weekday: WEEKDAY[get('weekday') || ''],
  }
}

export function localDateKey(now: Date | number | string = Date.now(), timeZone = LOCAL_TZ): string {
  const p = localParts(now, timeZone)
  return `${p.year}-${String(p.month).padStart(2, '0')}-${String(p.day).padStart(2, '0')}`
}

export function isFridayAfter1500(now: Date | number | string = Date.now(), timeZone = LOCAL_TZ): boolean {
  const p = localParts(now, timeZone)
  return p.weekday === 5 && p.hour >= 15
}

export function inFridayCheckInWindow(now: Date | number | string = Date.now(), timeZone = LOCAL_TZ): boolean {
  const p = localParts(now, timeZone)
  if (p.weekday === 5) return p.hour >= 15
  return p.weekday === 6 || p.weekday === 0
}

export function isDateOnly(value: unknown): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(String(value || ''))
}

export function hasExplicitTimezone(value: unknown): boolean {
  const s = String(value || '')
  return /Z$/i.test(s) || /[+-]\d{2}:?\d{2}(:\d{2})?$/.test(s)
}

/**
 * Instant fields are TIMESTAMPTZ in the database and serialize as UTC. Treat
 * legacy ISO datetime strings missing their offset as UTC too, never host local.
 * Reject other offsetless formats rather than letting Date guess a timezone.
 */
function instantDate(value: unknown): Date {
  if (value instanceof Date) return value
  if (typeof value === 'string' && !isDateOnly(value) && !hasExplicitTimezone(value)) {
    const match = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)$/)
    return match ? new Date(`${match[1]}T${match[2]}Z`) : new Date(NaN)
  }
  return new Date(value as Date | number | string)
}

export function calendarDateKey(value: unknown): string {
  if (value == null || value === '') return ''
  if (isDateOnly(value)) return String(value)
  if (typeof value === 'string') {
    const m = value.match(/^(\d{4}-\d{2}-\d{2})/)
    if (m) return m[1]
  }
  if (value instanceof Date && Number.isFinite(value.getTime())) {
    return value.toISOString().slice(0, 10)
  }
  const d = new Date(value as Date | number | string)
  if (Number.isFinite(d.getTime())) return d.toISOString().slice(0, 10)
  return ''
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function monthDayFromKey(key: string): string {
  const [y, m, d] = key.split('-').map(Number)
  if (!y || !m || !d) return ''
  return `${MONTHS[m - 1]} ${d}`
}

export function formatCalendarMonthDay(value: unknown): string {
  if (value == null || value === '') return ''
  const key = calendarDateKey(value)
  if (key) return monthDayFromKey(key)
  return String(value)
}

/** Instant label in company timezone; offsetless ISO datetimes are UTC. */
export function formatInstantMonthDay(value: unknown, timeZone = LOCAL_TZ): string {
  if (value == null || value === '') return ''
  if (isDateOnly(value)) {
    return formatCalendarMonthDay(value)
  }
  const d = instantDate(value)
  if (Number.isFinite(d.getTime())) {
    return monthDayFromKey(localDateKey(d, timeZone)) || String(value)
  }
  return String(value)
}

/** @deprecated Pass field kind — use formatCalendarMonthDay or formatInstantMonthDay. */
export function formatLocalMonthDay(value: unknown, timeZone = LOCAL_TZ): string {
  if (isDateOnly(value)) return formatCalendarMonthDay(value)
  return formatInstantMonthDay(value, timeZone)
}
